'use strict';
/* ==========================================================================
   core-local/till.js — the Sole Proprietor till (DESIGN.md §4). Pure: no DOM, no engine globals. Runs in node
   (module.exports: tools/tillcheck.js, tools/contentcheck.js) and in the page (window.Till). Registers itself with the
   question bank as `till` when core/bank.js is loaded first (lesson.json scripts order), so a pool formula may say
   till.usd.total(p, a, c) or till.successive(a, b).

   MONEY IS INTEGER CENTS everywhere in the model. Percents are plain numbers (20 = 20%). Rounding: every percent of a
   money amount is rounded HALF UP to the cent, in exact integer arithmetic (BigInt): Till.pct(cents, p).
   Facts (docs/topics/facts-percent-off.md, cited by row next to each rule below):
   - TAX = 6: Florida's general STATE rate (facts §2 row 1). Say "6% state tax, some counties add a bit more"
     (facts Corrections #7: county surtax 0–2%).
   - Tax is computed ONCE on the taxable subtotal, carried to the third decimal and rounded up when the third decimal
     is > 4 = round half up to the cent (s. 212.12(10)(a); facts §2 row 3 and "Our till rule"; Corrections #1). Rounding
     once on the invoice total is one of the two legal options (s. 212.12(10)(b), Corrections #2): we never call the
     per-item option "wrong".
   - Store discounts and STORE coupons come off BEFORE tax (Rule 12A-1.018(2), (4)(b); facts §3 rows 1–2,
     Corrections #4–#5). A manufacturer's coupon would not reduce the taxed price (facts §3 row 3): the lesson's
     coupons are all store coupons.

   ---- the maths helpers (numbers in, numbers out) ----
   Till.pct(cents, p)            → integer cents: p% of cents, half up            pct(12000, 6) = 720
   Till.discount(cents, p)       → the price after p% off (the discount amount is pct(cents, p))
   Till.markup(cents, p)         → the price after a p% markup (+ pct(cents, p))
   Till.tax(cents, rate = TAX)   → the tax on a taxable subtotal (= pct)
   Till.multiplier(p)            → 1 + p/100 for a SIGNED percent change: multiplier(-20) = 0.8, multiplier(6) = 1.06
   Till.offMultiplier(p)         → 1 − p/100 (p% off):                            offMultiplier(20) = 0.8
   Till.successive(p1, p2, …)    → the total percent OFF of discounts taken one after another (also takes one array):
                                   successive(50, 50) = 75, successive(20, 25) = 40
   Till.change(from, to)         → percent change FROM the original: change(100, 96) = −4 (any unit)
   Till.error(estimate, actual)  → percent error |e − a| / |a| × 100
   Till.simpleInterest(P, r, t)  → I = P × r × t in integer cents (P cents, r % a year, t years), half up
   Till.cents(dollars) / Till.dollars(cents) / Till.fmt(cents) "120.00" / "−24.00" / Till.money(cents) "$120.00"
   Till.floridaTax(cents, rate)  → the statute's algorithm, spelled out (3rd decimal, > 4 rounds up): tillcheck
                                   proves it equals Till.tax on a big sample.
   Till.usd.*                    → the same in DOLLARS for question-pool formulas (each step rounded like the till):
                                   usd.pct(d, p), usd.off(d, p1, p2…), usd.up(d, p1…), usd.markup(d, p), usd.tax(d, rate),
                                   usd.withTax(d, rate), usd.coupon(d, c), usd.interest(P, r, t),
                                   usd.total(d, pctOff = 0, couponD = 0, rate = TAX)  (the Florida order: %, coupon, tax)

   ---- receipts ----
   Till.receipt(spec) → { lines: [{label, cents, kind, why, pct?}], items, preDiscount, subtotal, tax, total, rate,
                          saved: {cents, pct} | null, spec, cheat: null }
     spec (cents): { items: [{label, cents, qty?}], markups?: [{label, pct}], discounts?: [{label, pct, bogo?}],
                     coupons?: [{label, cents}], tax?: 6, saved?: true }
     The honest order: items → markups → percent discounts (each on the RUNNING price; a bogo discount takes pct off
     the cheaper of the first two pairs) → store coupons (dollars) → SUBTOTAL → TAX (once, on the subtotal) → TOTAL →
     (optional) YOU SAVED $x (N%), N = savings ÷ the tag price before discounts (preDiscount).
     kind ∈ item | markup | discount | coupon | subtotal | tax | total | saved. Discount and coupon lines have negative
     cents. The saved line is not part of the sum.
   Till.fromSpec(poolSpec, params) → the honest receipt of a POOL receipt spec (content/bank.receipts.json: items
     [{label, price}], hype, bogo, discounts [{label, pct}], coupons [{label, dollars}], saved; params in DOLLARS;
     labels say {param}). Till.specFrom(poolSpec, params) → the cents spec. Format: comment above specFrom.
   Till.print(receipt, {header, sub, date}) → the NB.receipt line list (only prints the numbers the till computed).

   ---- the cheats (Receipt Check only) ----
   Till.cheat(name, receipt, poolSpec?, params?) → { name, receipt, wrongLine, accept, honestLine, honestIndex, honest, rule, misconception }
     (name 'legit' → the honest receipt itself, wrongLine −1, honestLine null, accept [])
     receipt = the WRONG receipt (every line after the wrong one follows from it, the till "believes" the cheat);
     wrongLine = the index in receipt.lines of the line that breaks the rule (where the red circle goes);
     accept = every line index a tap may count as right (= [wrongLine], except dollarCouponAfterTax: [the coupon
     line printed after the tax, the tax line it made wrong]); honestLine = the honest line object Rivera writes next
     to it ({label, cents, kind, why}; for dollarCouponAfterTax the honest TAX line, because the coupon's amount is
     right and only its place is wrong; for addedCoupons one synthesized line "50% THEN 50%" = the two honest lines);
     honestIndex = its index in honest.lines (−1 when synthesized).
   Till.CHEATS[name] = { needs(spec) → bool, misconception, rule }   Till.canCheat(name, spec)   Till.CHEAT_NAMES
     addedCoupons          two percent coupons added into one (50% + 50% = 100%)          percents-add
     taxOnOriginal         tax on the price before the discounts and coupons               order-matters
     decimalSlip           6% charged as 0.6%                                              decimal-slip
     backToStart           "+20% then 20% off = the old price": the discount taken of the price before the markup   back-to-start
     wrongBase             "you saved N%" computed from the sale price, not the original   wrong-base
     dollarCouponAfterTax  a store coupon taken off after tax (tax charged on the pre-coupon price)                 order-matters
     bogoBoth              "buy one, get one 50% off" applied as 50% off both pairs        wrong-base
   ========================================================================== */
(function (root) {
  const TAX = 6;                                   // facts §2 row 1: "Florida's general state sales tax rate is 6%"
  const BI = BigInt;

  /* ---------------- exact numbers ---------------- */
  const need = (c, what) => { if (!Number.isSafeInteger(c)) throw new Error(`till: ${what} must be whole cents (got ${c})`); return c; };
  const fin = (x, what) => { if (typeof x !== 'number' || !isFinite(x)) throw new Error(`till: ${what} must be a finite number (got ${x})`); return x; };
  const clean = x => Number((+x).toPrecision(12));                     // drop float noise: 0.8000000000000002 → 0.8
  /* a decimal percent as an exact fraction n / 10^k (6 → 6/1, 7.5 → 75/10, 0.6 → 6/10) */
  function frac(p) {
    fin(p, 'percent');
    for (let k = 0; k <= 8; k++) { const n = clean(p * Math.pow(10, k)); if (Number.isInteger(n)) return { n: BI(n), k }; }
    throw new Error(`till: percent ${p} has more than 8 decimals`);
  }
  /* p% of whole cents, rounded half up (away from zero for a negative amount) — exact, BigInt */
  function pct(cents, p) {
    need(cents, 'cents');
    const { n, k } = frac(p), D = 100n * 10n ** BI(k);
    const a = BI(cents) * n, s = a < 0n ? -1n : 1n, m = a < 0n ? -a : a;
    return Number(s * ((2n * m + D) / (2n * D)));
  }
  /* the statute, spelled out: "carried to the third decimal place … rounds up to the next cent whenever the third
     decimal place is greater than four" (s. 212.12(10)(a), facts §2 row 3). Tax in mils (tenths of a cent), truncated. */
  function floridaTax(cents, rate = TAX) {
    need(cents, 'cents'); if (cents < 0) throw new Error('till: floridaTax of a negative amount');
    const { n, k } = frac(rate);
    const mils = (BI(cents) * n * 10n) / (100n * 10n ** BI(k));        // the tax carried to the third decimal (dollars)
    const third = mils % 10n;
    return Number(mils / 10n + (third > 4n ? 1n : 0n));
  }
  const cents = d => { fin(d, 'dollars'); return Math.sign(d) * Math.round(clean(Math.abs(d) * 100)); };
  const dollars = c => need(c, 'cents') / 100;
  const fmt = c => { need(c, 'cents'); const a = Math.abs(c); return (c < 0 ? '−' : '') + String(Math.floor(a / 100)).replace(/\B(?=(\d{3})+$)/g, ',') + '.' + String(a % 100).padStart(2, '0'); };
  const money = c => (c < 0 ? '−$' + fmt(-c) : '$' + fmt(c));
  const pctText = p => (Number.isInteger(clean(p)) ? String(clean(p)) : clean(p).toFixed(1).replace(/\.0$/, '')).replace(/^-/, '−') + '%';

  /* ---------------- the maths helpers ---------------- */
  const discount = (c, p) => c - pct(c, p);
  const markup = (c, p) => c + pct(c, p);
  const tax = (c, rate = TAX) => pct(c, rate);
  const multiplier = p => clean(1 + fin(p, 'percent') / 100);
  const offMultiplier = p => clean(1 - fin(p, 'percent') / 100);
  const list = args => (args.length === 1 && Array.isArray(args[0]) ? args[0] : args);
  function successive(...args) {
    const ps = list(args); if (!ps.length) return 0;
    let keep = 1; for (const p of ps) keep *= (100 - fin(p, 'percent')) / 100;
    return clean(100 - keep * 100);
  }
  function change(from, to) { fin(from, 'from'); fin(to, 'to'); if (from === 0) throw new Error('till: change from 0'); return clean((to - from) / from * 100); }
  function error(estimate, actual) { fin(estimate, 'estimate'); fin(actual, 'actual'); if (actual === 0) throw new Error('till: error against 0'); return clean(Math.abs(estimate - actual) / Math.abs(actual) * 100); }
  function simpleInterest(P, r, t) { need(P, 'principal'); return pct(P, clean(fin(r, 'rate') * fin(t, 'time'))); }   // I = P × r × t

  /* the dollar face (question-pool formulas work in dollars; every step is rounded like the till) */
  const usd = {
    pct: (d, p) => dollars(pct(cents(d), p)),
    off: (d, ...ps) => dollars(list(ps).reduce((c, p) => discount(c, p), cents(d))),
    up: (d, ...ps) => dollars(list(ps).reduce((c, p) => markup(c, p), cents(d))),
    markup: (d, p) => dollars(markup(cents(d), p)),
    tax: (d, rate = TAX) => dollars(tax(cents(d), rate)),
    withTax: (d, rate = TAX) => { const c = cents(d); return dollars(c + tax(c, rate)); },
    coupon: (d, c) => dollars(Math.max(0, cents(d) - cents(c))),
    interest: (P, r, t) => dollars(simpleInterest(cents(P), r, t)),
    total: (d, pctOff = 0, couponD = 0, rate = TAX) => {
      let c = cents(d); if (pctOff) c = discount(c, pctOff);
      c = Math.max(0, c - cents(couponD));
      return dollars(c + tax(c, rate));
    },
  };

  /* ---------------- receipts ---------------- */
  function normSpec(spec) {
    if (!spec || !Array.isArray(spec.items) || !spec.items.length) throw new Error('till: a receipt needs items');
    const s = {
      items: spec.items.map(it => ({ label: String(it.label || 'ITEM'), cents: need(it.cents, 'item cents'), qty: it.qty == null ? 1 : need(it.qty, 'qty') })),
      markups: (spec.markups || []).map(m => ({ label: String(m.label || `MARKUP ${pctText(m.pct)}`), pct: fin(m.pct, 'markup pct') })),
      discounts: (spec.discounts || []).map(d => ({ label: String(d.label || `${pctText(d.pct)} OFF`), pct: fin(d.pct, 'discount pct'), bogo: !!d.bogo })),
      coupons: (spec.coupons || []).map(c => ({ label: String(c.label || 'STORE COUPON'), cents: need(c.cents, 'coupon cents') })),
      tax: spec.tax == null ? TAX : fin(spec.tax, 'tax rate'),
      saved: !!spec.saved,
    };
    s.items.forEach(it => { if (it.cents < 0 || it.qty < 1) throw new Error('till: item prices ≥ 0, qty ≥ 1'); });
    s.discounts.forEach(d => { if (d.pct < 0 || d.pct > 100) throw new Error('till: a discount is 0–100%'); });
    s.coupons.forEach(c => { if (c.cents < 0) throw new Error('till: a coupon is ≥ 0'); });
    if (s.discounts.some(d => d.bogo) && !(s.items.length >= 2 || s.items[0].qty >= 2)) throw new Error('till: a bogo discount needs two pairs');
    return s;
  }
  /* the unit prices of the first two pairs (a bogo deal): [first, second] */
  function pairs(s) {
    const u = [];
    for (const it of s.items) for (let q = 0; q < it.qty && u.length < 2; q++) u.push(it.cents);
    return u;
  }

  /* build a receipt; `m` (a cheat) bends exactly one rule and marks that line wrong */
  function build(spec, m = {}) {
    const s = normSpec(spec), lines = [];
    let wrong = -1;
    const add = (l, isWrong) => { lines.push(l); if (isWrong) wrong = lines.length - 1; return l; };
    // items
    for (const it of s.items) add({ label: it.qty > 1 ? `${it.qty} × ${it.label}` : it.label, cents: it.cents * it.qty, kind: 'item', why: it.qty > 1 ? `${it.qty} × ${fmt(it.cents)}` : 'the price on the tag' });
    const itemsTotal = lines.reduce((a, l) => a + l.cents, 0);
    let run = itemsTotal;
    // markups (the price goes UP before any sale)
    for (const mk of s.markups) { const a = pct(run, mk.pct); add({ label: mk.label, cents: a, kind: 'markup', pct: mk.pct, why: `+${pctText(mk.pct)} of ${fmt(run)}` }); run += a; }
    const preDiscount = run;                        // the tag price before any discount or coupon
    // percent discounts, each on the running price
    const ds = s.discounts.slice();
    for (let i = 0; i < ds.length; i++) {
      const d = ds[i];
      if (d.bogo) {
        const [p1, p2] = pairs(s), cheaper = Math.min(p1, p2);
        if (m.bogoBoth) { const a = Math.min(run, pct(p1 + p2, d.pct)); add({ label: d.label, cents: -a, kind: 'discount', pct: d.pct, bogo: true, why: `${pctText(d.pct)} of BOTH pairs (${fmt(p1 + p2)})` }, true); run -= a; }
        else { const a = Math.min(run, pct(cheaper, d.pct)); add({ label: d.label, cents: -a, kind: 'discount', pct: d.pct, bogo: true, why: `${pctText(d.pct)} off the cheaper pair (${fmt(cheaper)}) only` }); run -= a; }
        continue;
      }
      if (m.addedCoupons && !m._merged) {
        const j = ds.findIndex((x, k) => k > i && !x.bogo);
        if (j > i) {
          m._merged = true;
          const sum = d.pct + ds[j].pct, a = Math.min(run, pct(run, Math.min(100, sum)));
          add({ label: `COUPONS ${pctText(d.pct)} + ${pctText(ds[j].pct)}`, cents: -a, kind: 'discount', pct: sum, why: `${pctText(d.pct)} + ${pctText(ds[j].pct)} = ${pctText(sum)} of ${fmt(run)}` }, true);
          run -= a; ds.splice(j, 1); continue;
        }
      }
      if (m.backToStart && !m._back && s.markups.length) {
        m._back = true;
        const old = run - (preDiscount - itemsTotal);          // the running price without the markup
        const a = Math.min(run, pct(old, d.pct));
        add({ label: d.label, cents: -a, kind: 'discount', pct: d.pct, why: `${pctText(d.pct)} of the OLD price ${fmt(old)}, not the raised one` }, true);
        run -= a; continue;
      }
      const a = pct(run, d.pct);
      add({ label: d.label, cents: -a, kind: 'discount', pct: d.pct, why: `${pctText(d.pct)} of the running price ${fmt(run)}` });
      run -= a;
    }
    // store coupons: dollars off BEFORE tax (Rule 12A-1.018(4)(b), facts §3 row 2)
    const late = [];
    for (const c of s.coupons) {
      const a = Math.min(run, c.cents);
      if (m.dollarCouponAfterTax) { late.push({ label: c.label, cents: -a, kind: 'coupon', why: 'taken off AFTER tax' }); continue; }
      add({ label: c.label, cents: -a, kind: 'coupon', why: 'a store coupon comes off before tax (Rule 12A-1.018)' });
      run -= a;
    }
    const subtotal = run;
    add({ label: 'SUBTOTAL', cents: subtotal, kind: 'subtotal', why: 'the taxable price: after every store discount and coupon' });
    // tax: once, on the taxable subtotal, half up (s. 212.12(10), facts §2 row 3)
    let taxC, taxWhy, taxWrong = false;
    if (m.taxOnOriginal) { taxC = tax(preDiscount, s.tax); taxWhy = `${pctText(s.tax)} of the price BEFORE the discounts (${fmt(preDiscount)})`; taxWrong = true; }
    else if (m.decimalSlip) { taxC = tax(subtotal, s.tax / 10); taxWhy = `${pctText(s.tax / 10)} of ${fmt(subtotal)}: ${pctText(s.tax)} slipped a decimal place`; taxWrong = true; }
    else if (m.dollarCouponAfterTax) { const base = subtotal; taxC = tax(base, s.tax); taxWhy = `${pctText(s.tax)} of ${fmt(base)}: the coupon wasn't taken off first`; taxWrong = late.length > 0; }
    else { taxC = tax(subtotal, s.tax); taxWhy = `${pctText(s.tax)} of ${fmt(subtotal)}, once, rounded half up to the cent (Florida s. 212.12(10))`; }
    add({ label: `TAX ${pctText(s.tax)}`, cents: taxC, kind: 'tax', pct: s.tax, why: taxWhy }, taxWrong);
    let total = subtotal + taxC;
    for (const l of late) { add(l); total += l.cents; }
    add({ label: 'TOTAL', cents: total, kind: 'total', why: late.length ? 'subtotal + tax − the coupon' : 'subtotal + tax' });
    // you saved (against the tag price before the sale; the cheat: against the sale price)
    let saved = null;
    if (s.saved) {
      const sv = preDiscount - subtotal, base = m.wrongBase ? subtotal : preDiscount;
      const p = base > 0 ? clean(sv / base * 100) : 0;
      saved = { cents: sv, pct: p };
      add({ label: `YOU SAVED ${pctText(round1(p))}`, cents: sv, kind: 'saved', pct: p, why: m.wrongBase ? `${fmt(sv)} ÷ the SALE price ${fmt(subtotal)}` : `${fmt(sv)} ÷ the original price ${fmt(preDiscount)}` }, !!m.wrongBase);
    }
    return { lines, items: itemsTotal, preDiscount, subtotal, tax: taxC, total, rate: s.tax, saved, spec: s, cheat: null, wrong };
  }
  const round1 = p => Math.round(p * 10) / 10;

  function receipt(spec) { const r = build(spec); delete r.wrong; return r; }

  /* ---------------- the cheats ---------------- */
  const pctDiscounts = s => (s.discounts || []).filter(d => !d.bogo);
  const CHEATS = {
    addedCoupons: { misconception: 'percents-add', needs: s => pctDiscounts(s).length >= 2 && pctDiscounts(s).every(d => d.pct > 0),
      rule: 'Percent coupons don’t add: the second one takes its percent of what’s left.' },
    taxOnOriginal: { misconception: 'order-matters', needs: s => (s.discounts || []).length + (s.coupons || []).filter(c => c.cents > 0).length > 0,
      rule: 'Tax is on the price you actually pay, after the store’s discounts.' },
    decimalSlip: { misconception: 'decimal-slip', needs: () => true,
      rule: '6% is 0.06 of the price, not 0.006.' },
    backToStart: { misconception: 'back-to-start', needs: s => (s.markups || []).length > 0 && pctDiscounts(s).length > 0,
      rule: 'The discount is a percent of the NEW, higher price, so you don’t get back to the start.' },
    wrongBase: { misconception: 'wrong-base', needs: s => !!s.saved && ((s.discounts || []).length + (s.coupons || []).length > 0),
      rule: '“You saved” is a percent of the ORIGINAL price, not the sale price.' },
    dollarCouponAfterTax: { misconception: 'order-matters', needs: s => (s.coupons || []).some(c => c.cents > 0),
      rule: 'A store coupon comes off before tax, so you’re taxed on less.' },
    bogoBoth: { misconception: 'wrong-base', needs: s => (s.discounts || []).some(d => d.bogo),
      rule: 'Buy one, get one 50% off: only the second pair is half price.' },
  };
  const CHEAT_NAMES = Object.keys(CHEATS);
  const specOf = x => (x && x.spec && x.lines ? x.spec : x);
  const canCheat = (name, x) => { const c = CHEATS[name]; if (!c) return false; try { return c.needs(normSpec(specOf(x))); } catch (e) { return false; } };

  /* cheat(name, receiptOrCentsSpec)  or  cheat(name, receipt, poolSpec, params) (the pool form: the receipt is only
     used for its spec when it has one; poolSpec + params rebuild it). name 'legit' → the honest receipt, wrongLine −1. */
  function cheat(name, x, poolSpec, params) {
    const spec = x && x.spec && x.lines ? x.spec : poolSpec ? specFrom(poolSpec, params) : normSpec(x);
    if (name === 'legit') { const h = receipt(spec); return { name, receipt: h, wrongLine: -1, accept: [], honestLine: null, honestIndex: -1, honest: h, rule: null, misconception: null }; }
    const C = CHEATS[name];
    if (!C) throw new Error(`till: no cheat "${name}" (${CHEAT_NAMES.join(', ')})`);
    if (!C.needs(spec)) throw new Error(`till: cheat "${name}" can't apply to this receipt`);
    const honest = receipt(spec), bad = build(spec, { [name]: true });
    const wrongLine = bad.wrong; delete bad.wrong;
    bad.cheat = name;
    const wl = bad.lines[wrongLine];
    let honestIndex, honestLine;
    if (name === 'addedCoupons') {
      // the two honest percent lines, as one line Rivera can write next to the merged one
      const ix = honest.lines.map((l, i) => (l.kind === 'discount' && !l.bogo ? i : -1)).filter(i => i >= 0).slice(0, 2);
      const a = honest.lines[ix[0]], b = honest.lines[ix[1]];
      honestIndex = -1;
      honestLine = { label: `${pctText(a.pct)} THEN ${pctText(b.pct)}`, cents: a.cents + b.cents, kind: 'discount', why: `${fmt(-a.cents)} + ${fmt(-b.cents)}: the second ${pctText(b.pct)} is of what's left` };
    } else {
      // the same kind of line, same position among its kind
      const kindIx = bad.lines.slice(0, wrongLine).filter(l => l.kind === wl.kind).length;
      honestIndex = honest.lines.map((l, i) => (l.kind === wl.kind ? i : -1)).filter(i => i >= 0)[kindIx];
      honestLine = honest.lines[honestIndex];
    }
    const accept = [wrongLine];
    if (name === 'dollarCouponAfterTax') {
      // the rule broken is the coupon's PLACE (after tax); the number it breaks is the tax: circling either is right
      const ci = bad.lines.findIndex((l, i) => l.kind === 'coupon' && i > wrongLine);
      if (ci >= 0) { accept.unshift(ci); }
    }
    return { name, receipt: bad, wrongLine: accept[0], accept, honestLine, honestIndex, honest, rule: C.rule, misconception: C.misconception };
  }

  /* ---------------- pool specs (content/bank.receipts.json) → cents specs → receipts ----------------
     The pool's "receipt" (designer's format, params in DOLLARS, percents as numbers):
       items:     [{label, price: "<param>"}]            each item one line (the price is the line's price)
       hype:      "<param>"                              the price went UP this % first (a HYPE +u% line: backToStart)
       bogo:      50 | "<param>"                         the second of two identical items at that % off
       discounts: [{label, pct: "<param>"}]              in order, each on the running price
       coupons:   [{label, dollars: "<param>"}]          store coupons, before tax
       saved:     true                                   print YOU SAVED N% (of the tag price before the sale)
       tax:       6 (optional)
     A value is a number, a param name, or (with core/bank.js loaded) an expression over the params. Labels may say
     {param} (or {expr} with the bank). */
  function valueOf(v, params) {
    if (typeof v === 'number') return v;
    const k = String(v).trim();
    if (Object.prototype.hasOwnProperty.call(params, k)) return +params[k];
    if (/^-?\d+(\.\d+)?$/.test(k)) return +k;
    if (root.Bank && root.Bank.eval) return +root.Bank.eval(k, params);
    throw new Error(`till: "${k}" is not a param`);
  }
  const numText = x => (typeof x === 'number' ? String(clean(x)) : String(x));
  function labelOf(v, params) {
    if (v == null) return undefined;
    return String(v).replace(/\{([^{}]+)\}/g, (m, e) => {
      const k = e.trim();
      if (Object.prototype.hasOwnProperty.call(params, k)) return numText(params[k]);
      if (root.Bank && root.Bank.fill) return root.Bank.fill(m, params);
      throw new Error(`till: label "${v}": {${k}} is not a param`);
    });
  }
  function specFrom(ps, params = {}) {
    const ev = v => valueOf(v, params), lab = v => labelOf(v, params);
    const out = {
      items: (ps.items || []).map(it => ({ label: lab(it.label), cents: cents(ev(it.price)), qty: it.qty == null ? 1 : ev(it.qty) })),
      markups: ps.hype != null ? [{ label: `HYPE +${pctText(ev(ps.hype))}`, pct: ev(ps.hype) }] : (ps.markups || []).map(mk => ({ label: lab(mk.label), pct: ev(mk.pct) })),
      discounts: (ps.bogo != null ? [{ label: `BOGO 2ND ${pctText(ev(ps.bogo))} OFF`, pct: ev(ps.bogo), bogo: true }] : [])
        .concat((ps.discounts || []).map(d => ({ label: lab(d.label), pct: ev(d.pct), bogo: !!d.bogo }))),
      coupons: (ps.coupons || []).map(c => ({ label: lab(c.label), cents: cents(ev(c.dollars != null ? c.dollars : c.off)) })),
      tax: ps.tax == null ? TAX : ev(ps.tax),
      saved: !!ps.saved,
    };
    return normSpec(out);
  }
  /* the honest receipt of a pool spec with its params */
  const fromSpec = (ps, params = {}) => receipt(specFrom(ps, params));

  /* ---------------- printing (NB.receipt lines) ---------------- */
  function print(r, o = {}) {
    const out = [{ c: o.header || 'SOLE PROPRIETOR', bold: true }, { c: o.sub || 'sneakers since 2009', size: 17 }];
    if (o.date) out.push({ c: o.date, size: 17 });
    out.push({ rule: true });
    for (const l of r.lines) {
      if (l.kind === 'subtotal' || (l.kind === 'saved')) out.push({ rule: true });
      const row = { l: l.label, r: fmt(l.cents), kind: l.kind };
      if (l.kind === 'total') row.bold = true;
      out.push(row);
    }
    out.push({ barcode: true, l: o.barcode || 'sole' });
    return out;
  }

  /* ---------------- how each line was worked out (Receipt Check, DESIGN.md §5.5) ----------------
     Till.explain(receipt, i) → one line of working for receipt.lines[i], e.g. "TAX 6%: $60.00 × 0.06 = $3.60".
     It shows what THIS receipt's till actually did (a bent receipt explains its own wrong line: "× 0.006"), found from
     the receipt's own numbers only: each percent line's base is the candidate base that reproduces its amount exactly
     (the honest base first). Pure; money in cents. */
  const dec = p => String(clean(p / 100));                                  // 6 → "0.06", 0.6 → "0.006"
  const exact = (c, p) => { const ct = clean(c * p / 100); return Number.isInteger(ct) ? null : clean(ct / 100); };   // un-rounded dollars
  const times = (base, p, amt) => { const x = exact(base, p); return `${money(base)} × ${dec(p)} = ${x == null ? money(amt) : '$' + String(x) + ', rounds to ' + money(amt)}`; };
  function explain(r, i) {
    const L = r.lines, l = L[i];
    if (!l) return '';
    const before = L.slice(0, i), sub = L.findIndex(x => x.kind === 'subtotal');
    const inRun = (x, j) => (sub < 0 || j < sub) && ['item', 'markup', 'discount', 'coupon'].includes(x.kind);
    const run = before.reduce((a, x, j) => a + (inRun(x, j) ? x.cents : 0), 0);
    const items = L.filter(x => x.kind === 'item'), itemsTotal = items.reduce((a, x) => a + x.cents, 0);
    const pre = itemsTotal + L.filter(x => x.kind === 'markup').reduce((a, x) => a + x.cents, 0);
    const amt = Math.abs(l.cents), head = `${l.label}: `;
    const terms = xs => xs.map((x, k) => (k === 0 ? money(x.cents) : (x.cents < 0 ? '− ' : '+ ') + money(Math.abs(x.cents)))).join(' ');
    switch (l.kind) {
      case 'item': {
        const q = /^(\d+) × /.exec(l.label);
        return q ? `${head}${q[1]} × ${money(l.cents / +q[1])} = ${money(l.cents)}` : `${head}the price on the tag, ${money(l.cents)}`;
      }
      case 'markup': return head + times(run, l.pct, amt) + ' more';
      case 'discount': {
        const units = []; for (const it of items) { const q = /^(\d+) × /.exec(it.label), n = q ? +q[1] : 1; for (let k = 0; k < n && units.length < 2; k++) units.push(it.cents / n); }
        const cands = l.bogo ? [Math.min(...units), units[0] + units[1]] : [run, run - (pre - itemsTotal)];
        const b = cands.find(c => Number.isSafeInteger(c) && Math.min(run, pct(c, l.pct)) === amt);
        return b == null ? head + (l.why || money(-amt)) : head + times(b, l.pct, amt) + ' off';
      }
      case 'coupon': return `${head}${money(amt)} off` + (sub >= 0 && i > sub ? ', taken off after the tax' : ' before tax');
      case 'subtotal': { const xs = before.filter((x, j) => inRun(x, j)); return xs.length === 1 ? `${head}${money(l.cents)}, nothing taken off` : `${head}${terms(xs)} = ${money(l.cents)}`; }
      case 'tax': {
        const S = L[sub] ? L[sub].cents : run, rate = l.pct;
        for (const [b, p] of [[S, rate], [pre, rate], [S, rate / 10], [pre, rate / 10]]) if (pct(b, p) === l.cents) return head + times(b, p, l.cents);
        return head + (l.why || money(l.cents));
      }
      case 'total': { const xs = before.filter((x, j) => x.kind === 'subtotal' || x.kind === 'tax' || (x.kind === 'coupon' && j > sub)); return `${head}${terms(xs)} = ${money(l.cents)}`; }
      case 'saved': {
        const S = L[sub] ? L[sub].cents : run, b = [pre, S].find(c => c > 0 && clean(l.cents / c * 100) === l.pct);
        return b == null ? head + money(l.cents) : `${head}${money(l.cents)} ÷ ${money(b)} = ${round1(l.pct) === l.pct ? '' : 'about '}${pctText(round1(l.pct))}`;
      }
      default: return head + money(l.cents);
    }
  }
  /* Rivera's correction: the honest receipt's working, numbered, from the first line where the bent receipt goes wrong
     to the honest total (or the honest "you saved" line when that is the only wrong one). c = Till.cheat(…) → [string] */
  function fixSteps(c) {
    if (!c || !c.honest || c.wrongLine < 0) return [];
    const H = c.honest.lines, B = c.receipt.lines, same = (a, b) => a && b && a.kind === b.kind && a.label === b.label && a.cents === b.cents;
    let s = H.findIndex((h, k) => !same(h, B[k])); if (s < 0) return [];
    const ti = H.findIndex(h => h.kind === 'total'), end = s > ti ? H.length - 1 : ti;
    const out = [];
    for (let k = s; k <= end; k++) out.push(explain(c.honest, k));
    return out;
  }

  const Till = {
    explain, fixSteps,
    TAX, CHEATS, CHEAT_NAMES,
    pct, discount, markup, tax, floridaTax, multiplier, offMultiplier, successive, change, error, simpleInterest,
    cents, dollars, fmt, money, pctText, usd,
    receipt, cheat, canCheat, fromSpec, specFrom, print, normSpec,
  };
  root.Till = Till;
  if (root.Bank && typeof root.Bank.register === 'function') root.Bank.register('till', Till);
  if (typeof module !== 'undefined' && module.exports) module.exports = Till;
})(typeof window !== 'undefined' ? window : globalThis);
